import React, { useEffect, useRef } from 'react';
import { Animated, View } from 'react-native';
import Svg, { Path, Circle, Defs, LinearGradient, Stop, G } from 'react-native-svg';

interface NetworkIconProps {
  focused: boolean;
  color?: string;
  size?: number;
}

const AnimatedPath = Animated.createAnimatedComponent(Path);
const AnimatedCircle = Animated.createAnimatedComponent(Circle);
const AnimatedG = Animated.createAnimatedComponent(G);

const NetworkIcon: React.FC<NetworkIconProps> = ({ focused, color = '#000000', size = 24 }) => {
  const fillOpacity = useRef(new Animated.Value(focused ? 1 : 0)).current;
  const strokeOpacity = useRef(new Animated.Value(focused ? 0 : 1)).current;
  const scale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(fillOpacity, {
        toValue: focused ? 1 : 0,
        useNativeDriver: false,
        tension: 100,
        friction: 7,
      }),
      Animated.spring(strokeOpacity, {
        toValue: focused ? 0 : 1,
        useNativeDriver: false,
        tension: 100,
        friction: 7,
      }),
    ]).start();
  }, [focused]);

  return (
    <Animated.View style={{ transform: [{ scale }] }}>
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Defs>
          <LinearGradient id="networkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <Stop offset="0%" stopColor="#D4A655" stopOpacity="1" />
            <Stop offset="50%" stopColor="#C4963E" stopOpacity="1" />
            <Stop offset="100%" stopColor="#A67D2E" stopOpacity="1" />
          </LinearGradient>
        </Defs>
        
        {/* Filled version (focused) */}
        <AnimatedG opacity={fillOpacity}>
          <AnimatedCircle
            cx="9"
            cy="6"
            r="4"
            fill={focused ? "url(#networkGradient)" : color}
          />
          <AnimatedPath
            d="M15 12C16.66 12 17.99 10.66 17.99 9C17.99 7.34 16.66 6 15 6C13.34 6 12 7.34 12 9C12 10.66 13.34 12 15 12Z"
            fill={focused ? "url(#networkGradient)" : color}
          />
          <AnimatedPath
            d="M9 13C6.67 13 2 14.17 2 16.5V19H16V16.5C16 14.17 11.33 13 9 13Z"
            fill={focused ? "url(#networkGradient)" : color}
          />
          <AnimatedPath
            d="M15 13C14.71 13 14.38 13.02 14.03 13.05C15.19 13.89 16 15.02 16 16.5V19H22V16.5C22 14.17 17.33 13 15 13Z"
            fill={focused ? "url(#networkGradient)" : color}
          />
        </AnimatedG>
        
        {/* Outline version (not focused) */}
        <AnimatedG opacity={strokeOpacity}>
          <AnimatedPath
            d="M9 12C10.93 12 12.5 10.43 12.5 8.5C12.5 6.57 10.93 5 9 5C7.07 5 5.5 6.57 5.5 8.5C5.5 10.43 7.07 12 9 12ZM9 7C9.83 7 10.5 7.67 10.5 8.5C10.5 9.33 9.83 10 9 10C8.17 10 7.5 9.33 7.5 8.5C7.5 7.67 8.17 7 9 7Z"
            fill={color}
          />
          <AnimatedPath
            d="M9 13C6.67 13 2 14.17 2 16.5V19H16V16.5C16 14.17 11.33 13 9 13ZM14 17H4V16.5C4 15.9 6.69 15 9 15C11.31 15 14 15.9 14 16.5V17Z"
            fill={color}
          />
          <AnimatedPath
            d="M16.04 13.81C17.2 14.65 18 15.77 18 16.5V19H22V16.5C22 14.95 18.41 13.95 16.04 13.81Z"
            fill={color}
          />
          <AnimatedCircle
            cx="15"
            cy="8"
            r="2.5"
            stroke={color}
            strokeWidth="1.5"
            fill="none"
          />
        </AnimatedG>
      </Svg>
    </Animated.View>
  );
};

export default NetworkIcon;
